import React, { useState } from 'react';
import Hero from '../../organisms/Hero';
import PopularCategories from '../../organisms/PopularCategories';
import { trackEvent } from '../../utils';
import qs from 'querystring';
import Helmet from 'react-helmet';
import { RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import { getCategoryDetails, getModalQuestionsByCategory } from '../../selectors';
import { GetQuotesModal } from 'hui';
import { Redirect } from 'react-router-dom';
import { State } from '../../reducers';
import { useSetupSeoMetrics } from '../../utils/snowplow';
import { getUserInfoForGQ } from '../../selectors/user';
import { buildUrl } from '../../utils/helpers';
import { hipNewUrl } from '../../api';

const getPageTitle = (path, category) => {
  if (path.includes('category')) {
    return `Get Free ${category} Quotes - Australia`;
  }
  return 'hipages - Change the way you tradie';
};

const getSeoMetrics = (categoryDetails, metaName, pathname) => {
  const metaPersonPlural = categoryDetails.personPlural || 'tradies';
  return {
    canonicalLink: `https://hipages.com.au${pathname}`,
    h1: 'What other jobs can we help you with?',
    h2: `what customers say about hipages`,
    metaDescription: `Get${metaName} quotes from 3 reliable ${metaPersonPlural}. Free service, fast responses and no obligation to hire.`,
    title: `Get Free${metaName} Quotes - Australia | hipages.com.au`,
    seoName: categoryDetails.name,
    siteSection: 'Get Quotes Projects',
    siteSubsection: 'Get Quotes category',
    targetKeyword: '',
    robots: '',
  };
};

const getHeroData = (key, modalData, setModalData, categoryDetails) => {
  return {
    title: 'What other jobs can we help you with?',
    setModalData,
    initialValue: key || '',
    fallbackQuestions: modalData.questions,
    fallbackCategoryDetails: modalData.categoryDetails,
    heroImage: categoryDetails.thumbnail,
  };
};

const getFallbackCategoryDetails = category => {
  return {
    key: category || '',
    personPlural: 'tradies',
    id: '',
  };
};

const GQProjects: React.FC<RouteComponentProps<{ category?: string }>> = props => {
  const { location, match } = props;
  const { path, url, params } = match;
  const { search_str: queryCategory, project } = qs.parse(location.search.slice(1));
  const routeCategory = params.category;
  const category = routeCategory || queryCategory;
  const userInformation = useSelector(state => getUserInfoForGQ(state));
  const { loggedIn, name: full_name, email, phone } = userInformation;
  const userDetailsforModal = {
    full_name,
    email,
    phone,
  };
  //get state category and questions if path has category
  const stateCategoryDetails = useSelector(state => getCategoryDetails(state));
  const stateModalQuestions = useSelector(state => getModalQuestionsByCategory(state))[
    routeCategory
  ];

  //datalayer and SEO construction
  const metaName = stateCategoryDetails.name ? ` ${stateCategoryDetails.name}` : '';
  const pageTitle = getPageTitle(path, metaName);
  const seoMetrics = getSeoMetrics(stateCategoryDetails, metaName, location.pathname);
  useSetupSeoMetrics(seoMetrics);

  const stateSuggestedCategories = useSelector((state: State) => state.category.suggestionsList);

  //fallback questions to open empty modal if something unexpected happens
  const fallbackCategoryDetails = getFallbackCategoryDetails(category);
  const fallbackQuestions = { questions: [], tree: [] };
  const [modalData, setModalData] = useState({
    categoryDetails: stateCategoryDetails.key ? stateCategoryDetails : fallbackCategoryDetails,
    questions: stateModalQuestions || fallbackQuestions,
    isOpen: !!routeCategory,
  });

  const setIsOpen = (value: boolean) => {
    setModalData({ ...modalData, isOpen: value });
  };
  const { key, personPlural, id } = modalData.categoryDetails;
  const heroData = getHeroData(key, modalData, setModalData, stateCategoryDetails);

  const primaryCategory =
    !!stateSuggestedCategories &&
    stateSuggestedCategories.recommended &&
    stateSuggestedCategories.recommended[0];
  const suggestedCategories = primaryCategory
    ? stateSuggestedCategories.recommended.slice(1)
    : undefined;

  if (routeCategory && !stateCategoryDetails.name) {
    return <Redirect to={`/get_quotes_simple?search_str=${routeCategory}`} />;
  }
  return (
    <>
      {pageTitle && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={seoMetrics.metaDescription} />
          <meta property="og:title" content={seoMetrics.title} />
          <meta property="og:description" content={seoMetrics.metaDescription} />
        </Helmet>
      )}
      <Hero {...heroData} />
      <PopularCategories
        setModalData={setModalData}
        categories={suggestedCategories}
        primaryCategory={primaryCategory}
      />
      {modalData.isOpen && (
        <GetQuotesModal
          questions={modalData.questions}
          categoryId={id}
          category={key}
          categoryPlural={personPlural}
          skipLocationStep={true}
          open={modalData.isOpen}
          setOpen={setIsOpen}
          trackEvent={trackEvent.bind(this)}
          pathName={url}
          initialLocation=""
          isProject={project === '1'}
          userDetails={loggedIn ? userDetailsforModal : {}}
          jobPostUrl={buildUrl(hipNewUrl, '/api/get-quotes/directory/post-job')}
        />
      )}
    </>
  );
};

export default GQProjects;
